import { useContext, useMemo } from 'react';

import { bind } from 'bind-event-listener';
import { di } from 'react-magnetic-di';
import { fetchQuery, graphql, ReactRelayContext, type Environment } from 'react-relay';

import {
	MentionNodeDataProvider,
	type FetchMentionNodeData,
} from '@atlaskit/editor-plugin-mentions/mention-node-data-provider';
import type { MentionNodeData, MentionProvider } from '@atlaskit/mention/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { RovoAgentAri } from '@atlassian/ari';
import {
	normalizeAccountIdFromIdentityAri,
	ROVO_AGENT_UUID,
} from '@atlassian/conversation-assistant-utils/default-agent';
import { isEntityAnnotationExperimentEnabled } from '@atlassian/conversation-assistant-utils/isEntityAnnotationExperimentEnabled';

import type { useRovoMentionNodeDataProviderQuery } from './__generated__/useRovoMentionNodeDataProviderQuery.graphql';

type MentionAvatarAgent = {
	id: string;
	avatarUrl: string | null;
	identityAccountId?: string;
};

export type MentionAvatarAgentSource = {
	getAllAgents: (signal?: AbortSignal) => Promise<readonly MentionAvatarAgent[]>;
};

type UseRovoMentionNodeDataProviderOptions = {
	mentionResource: Promise<MentionProvider>;
	agentSource?: MentionAvatarAgentSource | null;
};

export type RovoMentionNodeDataProvider = MentionNodeDataProvider;

// Rovo hex logo, inlined from `@atlaskit/logo/raw-icons` `rovoHexIcon` to match the synthetic
// `@Rovo` picker row; that module bundles every logo data-URI and does not tree-shake.
const ROVO_HEX_LOGO_DATA_URI =
	"data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' viewBox='0 0 24 24'> <path fill='%231868db' fill-rule='evenodd' d='m13.227 16.789-7.154 4.169-3.403-1.96a2.72 2.72 0 0 1-1.362-2.355V7.366c0-.973.518-1.87 1.361-2.354l6.044-3.487-.038.114a3.6 3.6 0 0 0-.172 1.088v9.278c0 1.274.68 2.45 1.786 3.085z' clip-rule='evenodd'/> <path fill='%236a9a23' fill-rule='evenodd' d='m11.296 15.671-7.193 4.153 6.607 3.812a2.73 2.73 0 0 0 2.676.026l.048-.033a2.73 2.73 0 0 0 1.232-1.525 2.7 2.7 0 0 0 .127-.822v-3.594z' clip-rule='evenodd'/> <path fill='%23af59e1' fill-rule='evenodd' d='m21.477 5.003-4.404-2.539-5.904 4.89 2.69 1.556a3.56 3.56 0 0 1 1.785 3.086v9.277a3.6 3.6 0 0 1-.21 1.202l6.044-3.486a2.71 2.71 0 0 0 1.362-2.355V7.357c0-.97-.521-1.87-1.363-2.354' clip-rule='evenodd'/> <path fill='%23fca700' fill-rule='evenodd' d='M12.74 8.266 9.353 6.312V2.718c0-.283.044-.56.127-.821A2.73 2.73 0 0 1 10.71.372V.37a.4.4 0 0 0 .048-.033 2.73 2.73 0 0 1 2.676.026l6.499 3.75z' clip-rule='evenodd'/> </svg> ";

const fetchMentionDataFromRelay = async (
	environment: Environment | null,
	mentionIds: string[],
	signal: AbortSignal,
): Promise<Record<string, MentionNodeData>> => {
	if (!environment || mentionIds.length === 0 || signal.aborted) {
		return {};
	}

	try {
		const response = await new Promise<
			useRovoMentionNodeDataProviderQuery['response'] | null | undefined
		>((resolve, reject) => {
			let unbindAbortListener: (() => void) | null = null;
			let subscription: { unsubscribe: () => void } | null = null;
			let isSettled = false;
			const settle = (
				value: useRovoMentionNodeDataProviderQuery['response'] | null | undefined,
			) => {
				if (isSettled) {
					return;
				}
				isSettled = true;
				unbindAbortListener?.();
				subscription?.unsubscribe();
				resolve(value);
			};
			const fail = (error: Error) => {
				if (isSettled) {
					return;
				}
				isSettled = true;
				unbindAbortListener?.();
				subscription?.unsubscribe();
				reject(error);
			};

			unbindAbortListener = bind(signal, {
				type: 'abort',
				listener: () => settle(null),
				options: { once: true },
			});
			subscription = fetchQuery<useRovoMentionNodeDataProviderQuery>(
				environment,
				graphql`
					query useRovoMentionNodeDataProviderQuery($mentionIds: [ID!]!) {
						users(accountIds: $mentionIds) {
							accountId
							picture
							... on AppUser {
								appType
							}
						}
						agentStudio_agentsByIds(ids: $mentionIds) @optIn(to: "AgentStudio") {
							... on AgentStudioAssistant {
								id
								identityAccount {
									id
									picture
								}
							}
						}
					}
				`,
				{ mentionIds },
				{ fetchPolicy: 'store-or-network' },
			).subscribe({ next: settle, error: fail, complete: () => settle(null) });
			if (isSettled) {
				subscription.unsubscribe();
			}
		});

		const mentionIdSet = new Set(mentionIds);
		const dataByMentionId: Record<string, MentionNodeData> = {};

		(response?.users ?? []).forEach((user) => {
			if (user.picture && mentionIdSet.has(user.accountId)) {
				dataByMentionId[user.accountId] = {
					avatarUrl: user.picture,
					appType: user.appType,
				};
			}
		});
		(response?.agentStudio_agentsByIds ?? []).forEach((agent) => {
			const avatarUrl = agent?.identityAccount?.picture;
			if (!agent?.id || !avatarUrl) {
				return;
			}

			let studioAgentId = agent.id;
			try {
				studioAgentId = RovoAgentAri.parse(agent.id).agentId;
			} catch {
				// Some callers and test environments already expose the Studio UUID.
			}
			const identityAccountId = normalizeAccountIdFromIdentityAri(agent.identityAccount?.id);
			[studioAgentId, identityAccountId].forEach((id) => {
				if (typeof id === 'string' && mentionIdSet.has(id)) {
					dataByMentionId[id] = { avatarUrl, appType: 'agent' };
				}
			});
		});

		return dataByMentionId;
	} catch (error) {
		if (signal.aborted) {
			return {};
		}
		throw error;
	}
};

const fetchAgentsFromSource = async (
	agentSource: MentionAvatarAgentSource | null | undefined,
	mentionIds: string[],
	signal: AbortSignal,
): Promise<Record<string, MentionNodeData>> => {
	if (!agentSource || mentionIds.length === 0 || signal.aborted) {
		return {};
	}

	try {
		const mentionIdSet = new Set(mentionIds);
		const agents = await agentSource.getAllAgents(signal);
		if (signal.aborted) {
			return {};
		}
		const dataByMentionId: Record<string, MentionNodeData> = {};

		agents.forEach((agent) => {
			if (!agent.avatarUrl) {
				return;
			}
			const avatarUrl = agent.avatarUrl;

			const identityAccountId = normalizeAccountIdFromIdentityAri(agent.identityAccountId);
			[agent.id, identityAccountId].forEach((id) => {
				if (typeof id === 'string' && mentionIdSet.has(id)) {
					dataByMentionId[id] = { avatarUrl, appType: 'agent' };
				}
			});
		});

		return dataByMentionId;
	} catch (error) {
		if (signal.aborted) {
			return {};
		}
		throw error;
	}
};

const createFetchMentionNodeData = (
	environment: Environment | null,
	agentSource: MentionAvatarAgentSource | null | undefined,
): FetchMentionNodeData => {
	return async (requestedMentionIds, signal = new AbortController().signal) => {
		// The default Rovo mention node uses `ROVO_AGENT_UUID`, which is neither an Identity
		// account nor a Studio agent, so no lookup can resolve it. Serve its logo locally.
		const defaultRovoData: Record<string, MentionNodeData> =
			fg('rovo_chat_fix_default_rovo_icon') &&
			isExperimentEnabled('rovo_chat_mention_agents') &&
			requestedMentionIds.includes(ROVO_AGENT_UUID)
				? { [ROVO_AGENT_UUID]: { avatarUrl: ROVO_HEX_LOGO_DATA_URI, appType: 'agent' } }
				: {};
		const mentionIds = requestedMentionIds.filter((id) => defaultRovoData[id] === undefined);
		let relayData: Record<string, MentionNodeData> = {};
		let relayError: Error | undefined;
		try {
			relayData = await fetchMentionDataFromRelay(environment, mentionIds, signal);
		} catch (error) {
			relayError = error instanceof Error ? error : new Error(String(error));
		}
		const unresolvedMentionIds = mentionIds.filter((id) => relayData[id] === undefined);
		let agentData: Record<string, MentionNodeData> = {};
		let agentSourceError: Error | undefined;
		try {
			agentData = await fetchAgentsFromSource(agentSource, unresolvedMentionIds, signal);
		} catch (error) {
			agentSourceError = error instanceof Error ? error : new Error(String(error));
		}
		const dataByMentionId = { ...defaultRovoData, ...relayData, ...agentData };
		const hasUnresolvedMentions = mentionIds.some((id) => dataByMentionId[id] === undefined);
		const fetchError = relayError ?? agentSourceError;
		if (!signal.aborted && hasUnresolvedMentions && fetchError) {
			throw new Error(`Failed to load Rovo mention avatar data: ${fetchError.message}`);
		}

		return dataByMentionId;
	};
};

/**
 * Creates one mention-avatar provider per mounted Rovo editor or renderer.
 * Picker results populate a synchronous cache; cold ADF falls back to Relay,
 * then to the existing agent source for legacy Studio-id mention nodes.
 * Person entity treatment or the existing Rovo rollout enables provider work.
 * Editor controls avatar rendering for the consuming Chat context.
 */
export const useRovoMentionNodeDataProvider = ({
	mentionResource,
	agentSource,
}: UseRovoMentionNodeDataProviderOptions): RovoMentionNodeDataProvider | undefined => {
	di(fetchQuery);
	const environment = useContext(ReactRelayContext)?.environment ?? null;
	const mentionNodeDataProvider = useMemo(() => {
		if (
			!isEntityAnnotationExperimentEnabled('person') &&
			!isExperimentEnabled('platform_editor_rovo_editor_mention_node_avatar')
		) {
			return undefined;
		}

		return new MentionNodeDataProvider(createFetchMentionNodeData(environment, agentSource));
	}, [agentSource, environment]);
	return mentionNodeDataProvider;
};
